import { invoicesReducer, InvoicesState } from '@spike/invoices-action';
import { paymentsReducer, PaymentsState } from '@spike/payments-action';
import { connectRouter, routerMiddleware } from 'connected-react-router';
import { createBrowserHistory, History } from 'history';
import { alertsMiddleware } from 'middlewares/AlertsMiddleware';
import { addonServicesReducer } from 'reducers/addonServices/AddonServicesReducer';
import { AddonServicesState } from 'reducers/addonServices/AddonServicesState';
import { AgreementsState } from 'reducers/agreements/AgreementsState';
import { agreementsReducer } from 'reducers/agreements/AgrementsReducer';
import { alertsReducer } from 'reducers/alerts/AlertsReducer';
import { AlertsState } from 'reducers/alerts/AlertsState';
import { authReducer } from 'reducers/auth/AuthReducer';
import { AuthState } from 'reducers/auth/AuthState';
import { availabilityReducer } from 'reducers/availability/AvailabilityReducer';
import { AvailabilityState } from 'reducers/availability/AvailabilityState';
import { bookingsReducer } from 'reducers/bookings/BookingsReducer';
import { BookingsState } from 'reducers/bookings/BookingsState';
import { customerReducer } from 'reducers/customer/CustomerReducer';
import { CustomerState } from 'reducers/customer/CustomerState';
import { intakeFormReducer } from 'reducers/intakeForm/IntakeFormReducer';
import { IntakeFormState } from 'reducers/intakeForm/IntakeFormState';
import { marketplaceReducer } from 'reducers/marketplace/MarketplaceReducer';
import { MarketplaceState } from 'reducers/marketplace/MarketplaceState';
import { masterDataReducer } from 'reducers/masterData/MasterDataReducer';
import { MasterDataState } from 'reducers/masterData/MasterDataState';
import { onlineBookingReducer } from 'reducers/onlineBooking/OnlineBookingReducer';
import { OnlineBookingState } from 'reducers/onlineBooking/OnlineBookingState';
import { petsReducer } from 'reducers/pets/PetsReducer';
import { PetsState } from 'reducers/pets/PetsState';
import { servicesReducer } from 'reducers/services/ServicesReducer';
import { ServicesState } from 'reducers/services/ServicesState';
import { staffsReducer } from 'reducers/staffs/StaffsReducer';
import { StaffsState } from 'reducers/staffs/StaffsState';
import { applyMiddleware, combineReducers, compose, createStore, Store } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension';
import thunk from 'redux-thunk';
import { RootAction } from 'types/Actions';

export const history = createBrowserHistory();

export interface RootState {
    readonly auth: AuthState;
    readonly alerts: AlertsState;

    readonly masterData: MasterDataState;
    readonly marketplace: MarketplaceState;

    readonly pets: PetsState;
    readonly bookings: BookingsState;
    readonly staffs: StaffsState;
    readonly services: ServicesState;
    readonly addonServices: AddonServicesState;
    readonly customer: CustomerState;
    readonly agreements: AgreementsState;
    readonly availability: AvailabilityState;

    readonly intakeForm: IntakeFormState;
    readonly onlineBooking: OnlineBookingState;

    // libraries
    readonly invoices: InvoicesState;
    readonly payments: PaymentsState;
}

const createRootReducer = (history: History) =>
    combineReducers({
        router: connectRouter(history),
        auth: authReducer,
        alerts: alertsReducer,

        masterData: masterDataReducer,
        marketplace: marketplaceReducer,

        pets: petsReducer,
        bookings: bookingsReducer,
        staffs: staffsReducer,
        services: servicesReducer,
        addonServices: addonServicesReducer,
        customer: customerReducer,
        agreements: agreementsReducer,
        availability: availabilityReducer,

        intakeForm: intakeFormReducer,
        onlineBooking: onlineBookingReducer,

        // libraries
        invoices: invoicesReducer,
        payments: paymentsReducer
    });

const middleware = applyMiddleware(thunk, routerMiddleware(history), alertsMiddleware);

const composeEnhancers = process.env.NODE_ENV === 'development' ? composeWithDevTools({}) : compose;

export const store: Store<RootState, RootAction> = createStore(
    createRootReducer(history),
    composeEnhancers(middleware)
);

export const dispatch = (action: RootAction): RootAction => store.dispatch(action);
